/**
 * SurfaceStructure — picks the structure for a surface.
 *
 * This is the seam `Distribution.ts` left open. `DistributionStyle` has carried
 * PAGE and HUB since the placement spine landed, with HUB documented as
 * *"Renderer not yet built; the value is authorable so the choice is recorded
 * ahead of it."* This is where that choice finally resolves to a renderer.
 *
 * ## Style is LAYOUT. Chrome is CHANNEL. They are not the same axis.
 *
 * `DropPageRouter` decides chrome — whether the platform wrapper is on — from
 * the DISTRIBUTION CHANNEL, and deliberately does not read style. Reading style
 * as chrome is a bug that already shipped once: choosing the hub layout
 * silently stripped the navbar off a public page. This component is the other
 * half of that separation and must never look at chrome.
 *
 * ## Default is PAGE, and that is a safety property
 *
 * All 13,681 production Drops have `pov` null and no `distributions[]`, so
 * every one of them resolves to PAGE and renders exactly as it does today.
 * Nothing changes until a surface is deliberately given a style.
 */

import React from "react";
import type {DistributionStyle} from "@devour/client";
import {SURFACE_STYLE} from "./resolveStyle";
import {SurfaceSlot} from "./slots";
import {OccupancyState, StageState} from "./occupancy";
import {HubStructure} from "./structures/HubStructure";
import {PageStructure, PageBodyVariant} from "./structures/PageStructure";

export interface SurfaceStructureProps {
    style?: DistributionStyle | null;
    slots: readonly SurfaceSlot[];
    /**
     * HUB passes occupancy; PAGE has no contested column so it never needs it.
     * One signature serves both rather than forcing callers to branch.
     */
    renderSlot: (slot: SurfaceSlot, occupancy?: OccupancyState) => React.ReactNode;
    stage?: StageState;
    pinnedSlotId?: string | null;
    renderColumnControl?: (occupancy: OccupancyState) => React.ReactNode;
    /**
     * HUB-only. The surface's own bar and its pinned action bar — the anchor's
     * `header.hd` and `nav.ab`. PAGE has neither: a page-style surface wears
     * the platform's chrome, which is `DropPageRouter`'s decision, not a
     * section of the content.
     *
     * These were absent from this signature, so a caller could pass a top bar
     * and watch it silently not render — the dispatcher dropped it on the way
     * through and no type complained, because every prop here is optional.
     */
    chrome?: React.ReactNode;
    actionBar?: React.ReactNode;
    maxWidth?: number | "full";
    gutter?: 32 | 48;
    headerHeight?: number;
    railWidth?: number;
    body?: PageBodyVariant;
}

export const SurfaceStructure = ({
    style,
    slots,
    renderSlot,
    stage,
    pinnedSlotId,
    renderColumnControl,
    chrome,
    actionBar,
    ...page
}: SurfaceStructureProps) => {
    // SURFACE_STYLE, not the client enum — see the interop note in resolveStyle.ts
    if (style === SURFACE_STYLE.HUB) {
        return (
            <HubStructure
                slots={slots}
                stage={stage}
                pinnedSlotId={pinnedSlotId}
                renderSlot={renderSlot}
                renderColumnControl={renderColumnControl}
                chrome={chrome}
                actionBar={actionBar}
            />
        );
    }

    /* PAGE by default — and for every surface in production today. */
    return <PageStructure slots={slots} renderSlot={s => renderSlot(s)} {...page} />;
};
